import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  model,
  output,
  ViewEncapsulation,
  inject,
  DestroyRef,
  viewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '../button/button.component';
import { FaIconComponent } from '../../atoms';
import { IconProp } from '@fortawesome/angular-fontawesome/types';
import { ReactiveFormsModule, FormControl } from '@angular/forms';
import { faCircleXmark, faFileVideo } from '@fortawesome/free-solid-svg-icons';
import { KindPipe } from '../../../pipes';
import { MessageKind } from '@clientwire/api';
import { TextFieldModule } from '@angular/cdk/text-field';
import { FilesToUpload } from '../../../interfaces';
import { AutosizeModule } from 'ngx-autosize';
import { Subscription, tap, throttleTime } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export type InputAddon = {
  button?: boolean;
  icon?: IconProp;
  label?: string;
  vAlign?: 'center' | 'flex-end' | 'flex-start';
  disabled?: boolean;
};

@Component({
  selector: 'orbit-input',
  standalone: true,
  imports: [
    CommonModule,
    ButtonComponent,
    ReactiveFormsModule,
    FaIconComponent,
    KindPipe,
    TextFieldModule,
    AutosizeModule,
  ],
  templateUrl: './input.component.html',
  styleUrl: './input.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputComponent {
  #defaultController = new FormControl<string | null>(null);
  //#region Inputs
  addonLeft = input<InputAddon>();
  addonRight = input<InputAddon>();
  autocomplete = input<boolean>(true);
  controller = input<FormControl>(this.#defaultController);
  disabled = input<boolean, unknown>(false, { transform: booleanAttribute });
  inputId = input<string>();
  inputSourceId = input<string>();
  inputType = input<'text' | 'number' | 'email' | 'password' | 'textarea'>('text');
  large = input<boolean>(false);
  loading = input<boolean>(false);
  logger = input<any>(null);
  name = input<string>('');
  placeholder = input<string>();
  preventTyping = input<boolean>(false);
  rounded = input<boolean, unknown>(false, { transform: booleanAttribute });
  textareaAutosizeLimit = input<{ minRows: number; maxRows: number }>({ minRows: 1, maxRows: 10 });
  testId = input<string>();
  //#endregion

  //#region Models
  files = model<FilesToUpload[]>();
  inputValue = model<string>();
  //#endregion

  //#region Outputs
  addonButtonClicked = output<boolean>();
  addonIconClicked = output<boolean>();
  deleteAttachmentEvent = output<MessageKind>();
  enterPressed = output<boolean>();
  isTyping = output<boolean>();
  //#endregion

  //#region Variables
  closeIcon = faCircleXmark;
  videoFileIcon = faFileVideo;
  #allowedFileTypes: MessageKind[] = ['IMAGE', 'VIDEO', 'AUDIO'];
  #controllerSubscription: Subscription | null = null;
  #destroyRef = inject(DestroyRef);
  textareaRef = viewChild('textarea', { read: ElementRef });
  inputRef = viewChild('input', { read: ElementRef });
  //#endregion

  //#region Computed
  hasAddon = computed(() => this.#calculateHasAddon());
  //#endregion

  constructor() {
    effect(() => {
      if (this.controller()) this.subscribeToControllerValueChanges();
    });
    effect(() => {
      this.disabled() ? this.controller().disable() : this.controller().enable();
    });
    effect(() => {
      const inputValue = this.inputValue();
      if (inputValue) {
        this.#defaultController.setValue(inputValue);
      }
    });
    effect(() => {
      const files = this.files();
      if (files) {
        files.forEach((file, index) => {
          if (!file.src) {
            const reader = new FileReader();
            reader.onload = (e) => {
              this.files.update((files) => {
                if (files && files[index]) {
                  files[index].src = reader.result as string;
                  return [...files];
                } else {
                  return files;
                }
              });
            };
            reader.readAsDataURL(file.file);
          }
        });
      }
    });
    effect(() => {
      if (this.inputSourceId()) this.subscribeToControllerValueChanges();
    });
  }

  typing(event: Event) {
    if (this.preventTyping()) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  subscribeToControllerValueChanges() {
    if (this.#controllerSubscription) {
      this.#controllerSubscription.unsubscribe();
    }
    this.#controllerSubscription = this.controller()
      .valueChanges.pipe(
        throttleTime(5000),
        tap((data) => {
          return data;
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe({
        next: (data) => {
          if (data?.length > 0) {
            this.isTyping.emit(true);
          }
        },
        error: (err) => {
          if (this.logger()) {
            try {
              this.logger().error('Error: %s', err);
            } catch (e) {
              console.error('Error: %s', err);
            }
          }
        },
      });
  }

  emitEnterPressed($event: Event, preventDefault = false) {
    if (preventDefault) {
      $event.preventDefault();
      $event.stopPropagation();
    }
    if (!this.loading()) {
      this.enterPressed.emit(true);
    }
  }

  emitAddonButtonClicked() {
    // TODO: Improve this to know which addon button was triggering the event
    this.addonButtonClicked.emit(true);
  }
  emitAddonIconClicked() {
    // TODO: Improve this to know which addon icon was triggering the event
    this.addonIconClicked.emit(true);
  }

  focusInput(): void {
    if (this.inputType() === 'textarea') {
      this.textareaRef()?.nativeElement?.focus();
    } else {
      this.inputRef()?.nativeElement?.focus();
    }
  }

  deleteAttachment(index: number) {
    const file = this.files()?.[index];
    // Clean the local file array
    this.files.update((files) => {
      if (!files) return files;
      const array = [...files];
      array.splice(index, 1);
      return array;
    });
    // Clean the form input
    if (file) {
      const fileKind = file.file?.type.split('/')[0]?.toUpperCase();
      if (this.#allowedFileTypes.includes(fileKind as MessageKind)) {
        this.deleteAttachmentEvent.emit(fileKind as MessageKind);
      }
    }
  }

  #calculateHasAddon() {
    return (
      this.addonLeft()?.button ||
      this.addonLeft()?.icon ||
      this.addonLeft()?.label ||
      this.addonRight()?.button ||
      this.addonRight()?.icon ||
      this.addonRight()?.label
    );
  }
}
